import React, { useEffect, useMemo, useState } from 'react'
import { Avatar, Button, Card, Col, Modal, Row, Select, Space, Typography, Tag } from 'antd'
import { CalendarOutlined, UnorderedListOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { leaveService } from '../../common/service/leave.service'

type LeaveRecord = {
	id: string
	leaveType?: string
	startDate?: string
	endDate?: string
	overallStatus?: string
	requester?: { name?: string; photoUrl?: string | null }
}

type Holiday = { date: string; name: string }

const LeaveCalendar: React.FC = () => {
	const navigate = useNavigate()
	const now = dayjs()
	const [year, setYear] = useState(now.year())
	const [month, setMonth] = useState(now.month() + 1)
	const [leaves, setLeaves] = useState<LeaveRecord[]>([])
	const [holidays, setHolidays] = useState<Holiday[]>([])
	const [selectedCell, setSelectedCell] = useState<{
		dateLabel: string
		holiday?: string
		leaves: LeaveRecord[]
	} | null>(null)

	useEffect(() => {
		leaveService
			.fetchListing({ page: 1, limit: 200 })
			.then((r: any) => setLeaves(r?.data?.data || r?.data || []))
			.catch(() => setLeaves([]))
	}, [])

	useEffect(() => {
		leaveService
			.getHolidays(year)
			.then((r: any) => setHolidays(r?.data?.holidays || r?.holidays || []))
			.catch(() => setHolidays([]))
	}, [year])

	const calendarDays = useMemo(() => {
		const first = dayjs(`${year}-${String(month).padStart(2, '0')}-01`)
		const daysInMonth = first.daysInMonth()
		const startOffset = (first.day() + 6) % 7
		const cells: Array<{
			day?: number
			holiday?: string
			leaveCount?: number
			leaves?: LeaveRecord[]
			dateLabel?: string
		}> = []
		for (let i = 0; i < startOffset; i++) cells.push({})
		for (let d = 1; d <= daysInMonth; d++) {
			const current = dayjs(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`)
			const currentStr = current.format('YYYY-MM-DD')
			const dayLeaves = leaves.filter((l) => {
				if (!l.startDate || !l.endDate) return false
				return currentStr >= l.startDate && currentStr <= l.endDate
			})
			const leaveCount = dayLeaves.length
			const holiday = holidays.find((h) => h.date === currentStr)?.name
			cells.push({
				day: d,
				holiday,
				leaveCount,
				leaves: dayLeaves,
				dateLabel: current.format('DD MMM YYYY'),
			})
		}
		return cells
	}, [year, month, leaves, holidays])

	const statusColor = (status?: string) => {
		switch ((status || '').toLowerCase()) {
			case 'approved':
				return 'green'
			case 'rejected':
				return 'red'
			case 'cancelled':
				return 'default'
			default:
				return 'gold'
		}
	}

	return (
		<>
			<GlobalHeader />
			<div style={{ padding: 24 }}>
				<Card title='Leave Calendar'>
					<Row gutter={12} style={{ marginBottom: 12 }}>
						<Col>
							<Select value={year} onChange={setYear} style={{ width: 120 }}>
								{Array.from({ length: 7 }).map((_, i) => {
									const y = now.year() - 2 + i
									return (
										<Select.Option key={y} value={y}>
											{y}
										</Select.Option>
									)
								})}
							</Select>
						</Col>
						<Col>
							<Select value={month} onChange={setMonth} style={{ width: 140 }}>
								{Array.from({ length: 12 }).map((_, i) => (
									<Select.Option key={i + 1} value={i + 1}>
										{dayjs().month(i).format('MMMM')}
									</Select.Option>
								))}
							</Select>
						</Col>
						<Col>
							<Button onClick={() => { setYear(now.year()); setMonth(now.month() + 1) }}>Today</Button>
						</Col>
						<Col flex='auto' style={{ display: 'flex', justifyContent: 'flex-end' }}>
							<Space.Compact>
								<Button
									icon={<UnorderedListOutlined />}
									onClick={() => navigate('/leaves')}
								/>
								<Button type='primary' icon={<CalendarOutlined />} />
							</Space.Compact>
						</Col>
					</Row>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(7, minmax(120px, 1fr))',
							gap: 8,
						}}
					>
						{calendarDays.map((c, idx) => (
							<div
								key={idx}
								style={{
									border: '1px solid #e5e7eb',
									borderRadius: 8,
									minHeight: 90,
									padding: 8,
									background: c.holiday ? '#fff7e6' : '#fff',
									cursor: c.day ? 'pointer' : 'default',
								}}
								onClick={() => {
									if (!c.day) return
									setSelectedCell({
										dateLabel: c.dateLabel || '',
										holiday: c.holiday,
										leaves: c.leaves || [],
									})
								}}
							>
								{c.day ? (
									<>
										<Typography.Text strong>{c.day}</Typography.Text>
										<div style={{ marginTop: 4 }}>
											{c.holiday ? <Tag color='gold'>{c.holiday}</Tag> : null}
											{(c.leaveCount || 0) > 0 ? <Tag color='blue'>{c.leaveCount} leave</Tag> : null}
										</div>
									</>
								) : null}
							</div>
						))}
					</div>
				</Card>
			</div>
			<Modal
				open={!!selectedCell}
				onCancel={() => setSelectedCell(null)}
				footer={null}
				title={selectedCell?.dateLabel || 'Date Details'}
			>
				{selectedCell?.holiday ? (
					<div style={{ marginBottom: 12 }}>
						<Tag color='gold'>Holiday: {selectedCell.holiday}</Tag>
					</div>
				) : null}
				{selectedCell && selectedCell.leaves.length === 0 ? (
					<Typography.Text type='secondary'>No leave records on this day.</Typography.Text>
				) : null}
				<Space direction='vertical' style={{ width: '100%' }} size={10}>
					{selectedCell?.leaves.map((l) => (
						<div
							key={`${l.id}-${l.startDate}-${l.endDate}`}
							style={{
								border: '1px solid #e5e7eb',
								borderRadius: 8,
								padding: 10,
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								gap: 8,
							}}
						>
							<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
								<Avatar
									size={36}
									src={l.requester?.photoUrl || undefined}
									style={{ backgroundColor: '#dbeafe', color: '#1d4ed8' }}
								>
									{(l.requester?.name || '?').charAt(0).toUpperCase()}
								</Avatar>
								<div>
									<Typography.Text strong>{l.leaveType || 'Leave'}</Typography.Text>
									<div style={{ fontSize: 12, color: '#64748b' }}>
										{l.requester?.name || '-'} | {l.startDate} - {l.endDate}
									</div>
									<Tag color={statusColor(l.overallStatus)} style={{ marginTop: 6 }}>
										{l.overallStatus || 'Pending'}
									</Tag>
								</div>
							</div>
							<Button
								size='small'
								onClick={() => {
									setSelectedCell(null)
									navigate(`/leaves/${l.id}`)
								}}
							>
								View
							</Button>
						</div>
					))}
				</Space>
			</Modal>
		</>
	)
}

export default LeaveCalendar
